import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import client from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';

const NAV_ITEMS = [
  { path: '/', label: 'Odds Board', icon: 'M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm11 0h7v7h-7v-7z' },
  { path: '/predictions', label: 'Predictions', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
  { path: '/props', label: 'Prop Picks', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
  { path: '/bets', label: 'Bets', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { path: '/parlay', label: 'Parlay', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { path: '/learn', label: 'Learn', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  { path: '/patterns', label: 'Patterns', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { path: '/briefing', label: 'Briefing', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z' },
  { path: '/alerts', label: 'Alerts', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
  { path: '/admin/quota', label: 'API Quota', admin: true, icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z' },
  { path: '/admin/accuracy', label: 'Accuracy', admin: true, icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { path: '/admin/controls', label: 'Controls', admin: true, icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z' },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, displayName, isAdmin, signOut } = useAuth();
  const effectiveTier = useEffectiveTier();
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem('ai-sports-edge-sidebar');
    return stored === 'collapsed';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('ai-sports-edge-sidebar', collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  useEffect(() => {
    client.get('/api/value-alerts')
      .then((r) => setAlertCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => setAlertCount(0));
  }, []);

  // Close mobile drawer on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    function onClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('pointerdown', onClickOutside);
    return () => document.removeEventListener('pointerdown', onClickOutside);
  }, [userMenuOpen]);


  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-[60] lg:hidden p-1.5 rounded bg-white/80 dark:bg-[#0a0d14]/80 border border-[#E5E7EB] dark:border-[#1e2536] backdrop-blur-sm"
      >
        <svg className="w-4 h-4 text-[#6B7280] dark:text-[#8892a8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-[70] h-full flex flex-col border-r border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#0a0d14] transition-all duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ width: mobileOpen ? 180 : collapsed ? 68 : 180 }}
      >
        {/* Brand */}
        <div className={`flex items-center h-12 border-b border-[#E5E7EB] dark:border-[#1e2536] ${collapsed ? 'justify-center px-0' : 'px-3'}`}>
          <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileOpen(false)}>
            <div className="w-6 h-6 rounded bg-[#005BB3] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[10px] font-bold">AI</span>
            </div>
            {!collapsed && (
              <span className="text-[16px] font-semibold tracking-tight text-[#111827] dark:text-[#e2e8f0] group-hover:text-[#005BB3] dark:group-hover:text-[#3b82f6] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
                Sports Edge
              </span>
            )}
          </Link>
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-2 overflow-hidden">
          {NAV_ITEMS.filter((item) => !item.admin || isAdmin).map((item) => {
            const active = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            const isAlerts = item.path === '/alerts';

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`group relative flex items-center gap-2.5 mx-1.5 mb-0.5 rounded transition-colors duration-150 ${
                  collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2'
                } ${
                  active
                    ? 'bg-[#E8F0FE] dark:bg-[#005BB3]/15 text-[#005BB3] dark:text-[#3b82f6] font-semibold'
                    : 'text-[#6B7280] dark:text-[#505a6e] font-medium hover:text-[#4B5563] dark:hover:text-[#e2e8f0] hover:bg-[#F8F9FA] dark:hover:bg-[#151a25]'
                }`}
              >
                <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#005BB3] dark:text-[#3b82f6]' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>

                {!collapsed && (
                  <span className="text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>{item.label}</span>
                )}

                {/* Alert badge */}
                {isAlerts && alertCount > 0 && (
                  <span className={`flex items-center justify-center min-w-[14px] h-[14px] rounded-full bg-[#F86A38] text-white text-[8px] font-bold px-0.5 ${collapsed ? 'absolute -top-0.5 -right-0.5' : 'ml-auto'}`}>
                    {alertCount}
                  </span>
                )}

                {/* Collapsed tooltip */}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 rounded bg-[#111827] dark:bg-[#e2e8f0] text-white dark:text-[#111827] text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[80]">
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Upgrade CTA — only for free/pro users */}
        {(effectiveTier === 'free' || effectiveTier === 'pro') && (
          <div className="px-1.5 pb-1">
            <Link
              to="/pricing"
              onClick={() => setMobileOpen(false)}
              className={`group relative flex items-center gap-2.5 w-full rounded-md transition-colors duration-150 bg-[#005BB3]/10 hover:bg-[#005BB3]/20 text-[#005BB3] dark:text-[#3b82f6] font-medium ${
                collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2'
              }`}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z" />
              </svg>
              {!collapsed && (
                <span className="text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>
                  {effectiveTier === 'pro' ? 'Upgrade to Premium' : 'View Plans'}
                </span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 rounded bg-[#111827] dark:bg-[#e2e8f0] text-white dark:text-[#111827] text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[80]">
                  {effectiveTier === 'pro' ? 'Upgrade to Premium' : 'View Plans'}
                </span>
              )}
            </Link>
          </div>
        )}

        {/* Auth section */}
        <div className="border-t border-[#E5E7EB] dark:border-[#1e2536] py-2 px-1.5">
          {user ? (
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                className={`group relative flex items-center gap-2.5 w-full rounded transition-colors duration-150 text-[#6B7280] dark:text-[#505a6e] hover:text-[#4B5563] dark:hover:text-[#e2e8f0] hover:bg-[#F8F9FA] dark:hover:bg-[#151a25] ${
                  collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {!collapsed && (
                  <span className="text-[13px] truncate" style={{ fontFamily: 'var(--font-sans)' }}>{displayName}</span>
                )}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 rounded bg-[#111827] dark:bg-[#e2e8f0] text-white dark:text-[#111827] text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[80]">
                    {displayName}
                  </span>
                )}
              </button>
              {userMenuOpen && (
                <div className="absolute bottom-full mb-1 left-0 right-0 mx-1 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] shadow-lg z-[80] overflow-hidden"
                  style={{ minWidth: collapsed ? 160 : undefined, left: collapsed ? 0 : undefined }}>
                  <div className="px-3 py-2 border-b border-[#E5E7EB] dark:border-[#1e2536]">
                    <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] truncate" style={{ fontFamily: 'var(--font-sans)' }}>{user.email}</p>
                  </div>
                  <Link to="/settings" onClick={() => { setUserMenuOpen(false); setMobileOpen(false); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-[13px] text-[#111827] dark:text-[#e2e8f0] hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150"
                    style={{ fontFamily: 'var(--font-sans)' }}>
                    <svg className="w-3.5 h-3.5 text-[#6B7280] dark:text-[#8892a8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </Link>
                  <button
                    onClick={() => { setUserMenuOpen(false); signOut(); }}
                    className="w-full text-left px-3 py-2 text-[13px] text-[#DC2626] hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150"
                    style={{ fontFamily: 'var(--font-sans)' }}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/auth"
              onClick={() => setMobileOpen(false)}
              className={`group relative flex items-center gap-2.5 rounded transition-colors duration-150 text-[#6B7280] dark:text-[#505a6e] font-medium hover:text-[#4B5563] dark:hover:text-[#e2e8f0] hover:bg-[#F8F9FA] dark:hover:bg-[#151a25] ${
                collapsed ? 'justify-center px-0 py-2' : 'px-2.5 py-2'
              }`}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
              {!collapsed && (
                <span className="text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>Sign In</span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 rounded bg-[#111827] dark:bg-[#e2e8f0] text-white dark:text-[#111827] text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[80]">
                  Sign In
                </span>
              )}
            </Link>
          )}
        </div>

        {/* Collapse toggle */}
        <div className="border-t border-[#E5E7EB] dark:border-[#1e2536] py-2 px-2 hidden lg:block">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="w-full flex items-center justify-center py-1.5 rounded text-[#6B7280] dark:text-[#505a6e] hover:text-[#4B5563] dark:hover:text-[#e2e8f0] hover:bg-[#F8F9FA] dark:hover:bg-[#151a25] transition-colors duration-150"
          >
            <svg className={`w-4 h-4 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </aside>
    </>
  );
}
